import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiShoppingCart,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiTruck,
  FiLock
} from "react-icons/fi";
import "./cart.css";

const API_BASE = "https://api.thetotetherapy.com";
const DELIVERY_FEE = 20;

// OFFER RULE:
// ONLY when same product quantity is EXACTLY 2 => +1 FREE (deliver 3)
// Any other qty => NO FREE
const applyOffer = (cartItems) => {
  return cartItems.map((item) => {
    const qty = Number(item.quantity || 0); // user selected (paid qty)
    const price = Number(item.price || 0);

    const freeQty = qty === 2 ? 1 : 0;
    const deliverQty = qty + freeQty;
    const lineTotal = qty * price; // payment only for selected qty

    return { ...item, freeQty, deliverQty, lineTotal };
  });
};

export default function Cart() {
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(stored);
  }, []);

  const updateQuantity = (id, type) => {
    const updated = cart.map((item) => {
      if (item.id === id) {
        const current = Number(item.quantity || 1);
        if (type === "inc") item.quantity = current + 1;
        if (type === "dec" && current > 1) item.quantity = current - 1;
      }
      return item;
    });

    setCart(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
  };

  const removeItem = (id) => {
    const updated = cart.filter((item) => item.id !== id);
    setCart(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
  };

  // apply offer (only affects display + totals)
  const discounted = applyOffer(cart);

  const subtotal = discounted.reduce((sum, item) => sum + item.lineTotal, 0);
  const delivery = discounted.length > 0 ? DELIVERY_FEE : 0;
  const total = subtotal + delivery;

  if (cart.length === 0) {
    return (
      <main className="cartPage">
        <div className="emptyCart">
          <FiShoppingCart size={40} />
          <h2>Looks like your calm hasn’t been added yet.</h2>
          <p>Discover intentional designs made for everyday life.</p>
          <a href="/shop" className="continueBtn">
            [ Explore the Collection ]
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="cartPage">
      <div className="cartContainer">
        <h1 className="cartTitle">
          <FiShoppingCart /> Shopping Cart
        </h1>

        <div className="cartGrid">
          {/* ITEMS */}
          <div className="cartItems">
            {discounted.map((item) => (
              <div className="cartItem" key={item.id}>
                <img src={`${API_BASE}${item.image}`} alt={item.title} />

                <div className="cartInfo">
                  <h3>{item.title}</h3>
                  <p className="itemPrice">AED {Number(item.price).toFixed(2)}</p>

                  {/* Offer note: only when qty == 2 */}
                  {item.freeQty === 1 && (
                    <p style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
                      🎁 +1 FREE (You’ll receive {item.deliverQty} total)
                    </p>
                  )}

                  <div className="qtyControls">
                    <button onClick={() => updateQuantity(item.id, "dec")}>
                      <FiMinus />
                    </button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, "inc")}>
                      <FiPlus />
                    </button>
                  </div>
                </div>

                {/* Payable total (only selected qty) */}
                <div className="itemTotal">AED {item.lineTotal.toFixed(2)}</div>

                <button className="removeBtn" onClick={() => removeItem(item.id)}>
                  <FiTrash2 />
                </button>
              </div>
            ))}
          </div>

          {/* SUMMARY */}
          <div className="cartSummary">
            <h2>Order Summary</h2>

            <div className="summaryRow">
              <span>Subtotal</span>
              <span>AED {subtotal.toFixed(2)}</span>
            </div>

            <div className="summaryRow">
              <span>
                <FiTruck /> Delivery
              </span>
              <span>AED {delivery.toFixed(2)}</span>
            </div>

            <div className="summaryDivider"></div>

            <div className="summaryRow totalRow">
              <span>Total</span>
              <span>AED {total.toFixed(2)}</span>
            </div>

            <button className="checkoutBtn" onClick={() => navigate("/checkout")}>
              <FiLock /> Secure Checkout
            </button>

            <p className="secureNote">
              Your payment information is encrypted and secure.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}